import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { 
  StickyNote,
  Plus,
  Edit,
  Trash2,
  Bell,
  BellOff,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  Circle,
  Search,
  Filter,
  Save,
  X,
  Tag,
  Flag,
  Archive,
  Pin,
  PinOff,
  Palette,
  AlarmClock
} from 'lucide-react';

const BlocNotes = () => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showCompleted, setShowCompleted] = useState(true);
  
  const [newNote, setNewNote] = useState({
    titre: '',
    contenu: '',
    categorie: 'personnel',
    priorite: 'normale',
    couleur: 'yellow',
    date_echeance: '',
    heure_echeance: '',
    notification_enabled: true,
    notification_minutes: 15,
    epingle: false,
    terminee: false
  });

  const categories = [
    { value: 'personnel', label: 'Personnel', color: 'bg-blue-100 text-blue-800' },
    { value: 'travail', label: 'Travail', color: 'bg-green-100 text-green-800' },
    { value: 'rdv', label: 'Rendez-vous', color: 'bg-purple-100 text-purple-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' },
    { value: 'idee', label: 'Idée', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'projet', label: 'Projet', color: 'bg-indigo-100 text-indigo-800' }
  ];

  const priorites = [
    { value: 'faible', label: 'Faible', color: 'text-gray-500', icon: '●' },
    { value: 'normale', label: 'Normale', color: 'text-blue-500', icon: '●' },
    { value: 'haute', label: 'Haute', color: 'text-orange-500', icon: '●' },
    { value: 'critique', label: 'Critique', color: 'text-red-500', icon: '●' }
  ];

  const couleurs = [
    { value: 'yellow', label: 'Jaune', class: 'bg-yellow-100 border-yellow-300' },
    { value: 'blue', label: 'Bleu', class: 'bg-blue-100 border-blue-300' },
    { value: 'green', label: 'Vert', class: 'bg-green-100 border-green-300' },
    { value: 'purple', label: 'Violet', class: 'bg-purple-100 border-purple-300' },
    { value: 'pink', label: 'Rose', class: 'bg-pink-100 border-pink-300' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-100 border-orange-300' }
  ];

  useEffect(() => {
    loadNotes();
  }, []);

  useEffect(() => {
    filterNotes();
  }, [notes, searchTerm, filterCategory, filterPriority, showCompleted]);

  const loadNotes = () => {
    const savedNotes = localStorage.getItem(`bloc_notes_${user?.id || 'default'}`);
    if (savedNotes) {
      const parsedNotes = JSON.parse(savedNotes);
      setNotes(parsedNotes);
    }
  };

  const saveNotes = (updatedNotes) => {
    localStorage.setItem(`bloc_notes_${user?.id || 'default'}`, JSON.stringify(updatedNotes));
    setNotes(updatedNotes);
  };

  const filterNotes = () => {
    let filtered = [...notes];

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(note =>
        note.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.contenu.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre par catégorie
    if (filterCategory !== 'all') {
      filtered = filtered.filter(note => note.categorie === filterCategory);
    }

    // Filtre par priorité
    if (filterPriority !== 'all') {
      filtered = filtered.filter(note => note.priorite === filterPriority);
    }

    // Filtre notes terminées
    if (!showCompleted) {
      filtered = filtered.filter(note => !note.terminee);
    }

    // Trier par épinglage puis par date de création
    filtered.sort((a, b) => {
      if (a.epingle && !b.epingle) return -1;
      if (!a.epingle && b.epingle) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    setFilteredNotes(filtered);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const noteData = {
      ...newNote,
      id: editingNote ? editingNote.id : Date.now().toString(),
      created_at: editingNote ? editingNote.created_at : new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    let updatedNotes;
    if (editingNote) {
      updatedNotes = notes.map(note => note.id === editingNote.id ? noteData : note);
    } else {
      updatedNotes = [noteData, ...notes];
    }

    saveNotes(updatedNotes);
    resetForm();
  };

  const resetForm = () => {
    setNewNote({
      titre: '',
      contenu: '',
      categorie: 'personnel',
      priorite: 'normale',
      couleur: 'yellow',
      date_echeance: '',
      heure_echeance: '',
      notification_enabled: true,
      notification_minutes: 15,
      epingle: false,
      terminee: false
    });
    setShowAddForm(false);
    setEditingNote(null);
  };

  const handleEdit = (note) => {
    setNewNote({ ...note });
    setEditingNote(note);
    setShowAddForm(true);
  };

  const handleDelete = (noteId) => {
    const updatedNotes = notes.filter(note => note.id !== noteId);
    saveNotes(updatedNotes);
  };

  const toggleCompleted = (noteId) => {
    const updatedNotes = notes.map(note =>
      note.id === noteId ? { ...note, terminee: !note.terminee } : note
    );
    saveNotes(updatedNotes);
  };

  const togglePinned = (noteId) => {
    const updatedNotes = notes.map(note =>
      note.id === noteId ? { ...note, epingle: !note.epingle } : note
    );
    saveNotes(updatedNotes);
  };

  const getPriorityDisplay = (priorite) => {
    const priority = priorites.find(p => p.value === priorite);
    return priority || priorites[1];
  };

  const getCategoryDisplay = (categorie) => {
    const category = categories.find(c => c.value === categorie);
    return category || categories[0];
  };

  const getCouleurClass = (couleur) => {
    const color = couleurs.find(c => c.value === couleur);
    return color?.class || couleurs[0].class;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isOverdue = (date_echeance, heure_echeance) => {
    if (!date_echeance) return false;
    const echeance = new Date(`${date_echeance}${heure_echeance ? `T${heure_echeance}` : ''}`);
    return echeance < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <StickyNote className="w-6 h-6" />
            Bloc-notes
          </h1>
          <p className="text-gray-600">Vos pense-bêtes et rappels personnalisés</p>
        </div>
        
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouvelle Note
        </button>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <StickyNote className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Notes</p>
              <p className="text-lg font-semibold text-gray-900">{notes.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">En Attente</p>
              <p className="text-lg font-semibold text-orange-600">
                {notes.filter(n => !n.terminee).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Terminées</p>
              <p className="text-lg font-semibold text-green-600">
                {notes.filter(n => n.terminee).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">En Retard</p>
              <p className="text-lg font-semibold text-red-600">
                {notes.filter(n => !n.terminee && isOverdue(n.date_echeance, n.heure_echeance)).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Recherche */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher dans vos notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filtres */}
          <div className="flex gap-3 flex-wrap">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Toutes catégories</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>

            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Toutes priorités</option>
              {priorites.map(prio => (
                <option key={prio.value} value={prio.value}>{prio.label}</option>
              ))}
            </select>

            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">Afficher terminées</span>
            </label>
          </div>
        </div>
      </div>

      {/* Grille des notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredNotes.map((note) => {
          const priority = getPriorityDisplay(note.priorite);
          const category = getCategoryDisplay(note.categorie);
          const isOverdueNote = isOverdue(note.date_echeance, note.heure_echeance);

          return (
            <div
              key={note.id}
              className={`relative p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all ${getCouleurClass(note.couleur)} ${
                note.terminee ? 'opacity-60' : ''
              } ${isOverdueNote && !note.terminee ? 'ring-2 ring-red-300' : ''}`}
            >
              {/* Header de la note */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleCompleted(note.id)}
                    className={`p-1 rounded-full hover:bg-white/50 transition-colors ${
                      note.terminee ? 'text-green-600' : 'text-gray-400'
                    }`}
                  >
                    {note.terminee ? <CheckCircle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  </button>
                  
                  <span className={`text-sm ${priority.color}`}>
                    {priority.icon}
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  {note.epingle && (
                    <Pin className="w-4 h-4 text-gray-600" />
                  )}
                  
                  {note.notification_enabled && note.date_echeance && (
                    <Bell className="w-4 h-4 text-blue-600" />
                  )}

                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => togglePinned(note.id)}
                      className="p-1 hover:bg-white/50 rounded transition-colors"
                    >
                      {note.epingle ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                    </button>
                    
                    <button
                      onClick={() => handleEdit(note)}
                      className="p-1 hover:bg-white/50 rounded transition-colors"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="p-1 hover:bg-white/50 rounded transition-colors text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Titre */}
              <h3 className={`font-semibold text-gray-900 mb-2 ${note.terminee ? 'line-through' : ''}`}>
                {note.titre}
              </h3>

              {/* Contenu */}
              <p className={`text-gray-700 text-sm mb-3 ${note.terminee ? 'line-through' : ''}`}>
                {note.contenu}
              </p>

              {/* Footer */}
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full ${category.color}`}>
                    {category.label}
                  </span>
                  
                  {note.date_echeance && (
                    <span className={`flex items-center gap-1 ${
                      isOverdueNote && !note.terminee ? 'text-red-600 font-medium' : 'text-gray-600'
                    }`}>
                      <Calendar className="w-3 h-3" />
                      {formatDate(note.date_echeance)}
                      {note.heure_echeance && ` ${note.heure_echeance}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredNotes.length === 0 && (
        <div className="text-center py-12">
          <StickyNote className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">
            {searchTerm || filterCategory !== 'all' || filterPriority !== 'all' 
              ? 'Aucune note ne correspond à vos critères'
              : 'Aucune note pour le moment'
            }
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Créer votre première note
          </button>
        </div>
      )}

      {/* Modal d'ajout/modification */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingNote ? 'Modifier la note' : 'Nouvelle note'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre *
                </label>
                <input
                  type="text"
                  required
                  value={newNote.titre}
                  onChange={(e) => setNewNote(prev => ({ ...prev, titre: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Titre de votre note"
                />
              </div>

              {/* Contenu */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contenu
                </label>
                <textarea
                  rows="4"
                  value={newNote.contenu}
                  onChange={(e) => setNewNote(prev => ({ ...prev, contenu: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Décrivez votre pense-bête..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie
                  </label>
                  <select
                    value={newNote.categorie}
                    onChange={(e) => setNewNote(prev => ({ ...prev, categorie: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Priorité */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priorité
                  </label>
                  <select
                    value={newNote.priorite}
                    onChange={(e) => setNewNote(prev => ({ ...prev, priorite: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {priorites.map(prio => (
                      <option key={prio.value} value={prio.value}>{prio.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Couleur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur
                </label>
                <div className="flex gap-2">
                  {couleurs.map(couleur => (
                    <button
                      key={couleur.value}
                      type="button"
                      onClick={() => setNewNote(prev => ({ ...prev, couleur: couleur.value }))}
                      className={`w-8 h-8 rounded-lg border-2 ${couleur.class} ${
                        newNote.couleur === couleur.value ? 'ring-2 ring-blue-500' : ''
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date d'échéance */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    value={newNote.date_echeance}
                    onChange={(e) => setNewNote(prev => ({ ...prev, date_echeance: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Heure */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Heure
                  </label>
                  <input
                    type="time"
                    value={newNote.heure_echeance}
                    onChange={(e) => setNewNote(prev => ({ ...prev, heure_echeance: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Notifications */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 font-medium text-gray-700">
                    <Bell className="w-4 h-4" />
                    Notifications
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newNote.notification_enabled}
                      onChange={(e) => setNewNote(prev => ({ ...prev, notification_enabled: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {newNote.notification_enabled && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Me rappeler avant (minutes)
                    </label>
                    <select
                      value={newNote.notification_minutes}
                      onChange={(e) => setNewNote(prev => ({ ...prev, notification_minutes: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={5}>5 minutes</option>
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 heure</option>
                      <option value={120}>2 heures</option>
                      <option value={1440}>1 jour</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newNote.epingle}
                    onChange={(e) => setNewNote(prev => ({ ...prev, epingle: e.target.checked }))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Épingler cette note</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {editingNote ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlocNotes;